/**
 * Brand-grid exclusion denylist.
 *
 * Some rows in the `brands` table are not refrigeration/appliance makers at
 * all — they leaked in from novelty products (mini-fridges printed with a
 * beverage or entertainment logo, tire-branded coolers, etc.) or are parse
 * artifacts where a product spec token ("WiFi", "Inverter") was mistaken for a
 * manufacturer. These are real brands of *something*, just not of appliances,
 * so they must never appear in a "shop by manufacturer" / "explore other
 * brands" grid alongside Whirlpool, GE, Bosch…
 *
 * This is a JUDGMENT-CURATED list, not a rule: a genuine appliance/kitchen
 * brand with only a handful of SKUs (e.g. Vinturi, Spa Sciences, Nostalgia)
 * STAYS. Only a name that is unambiguously not an appliance manufacturer —
 * a soft-drink, a beer, an entertainment property, a car/tire marque, or a
 * non-brand feature token — goes here.
 *
 * We DO NOT delete the brand rows or their products; the products remain
 * reachable through search, collections and their own product pages. What is
 * suppressed is the BRAND as a browsable entity, at the data layer.
 * Keyed by `brands.slug` (stable, unique, lowercased).
 *
 * Reach, as of 07/29/2026 — four surfaces, not the original one:
 *   - `/brands` index and `/api/brands` (the logo/brand grids)
 *   - `/sitemap/brands.xml`
 *   - `/brands/[slug]` ITSELF, which redirects 308 to /brands
 *
 * That last one closed a real gap (FRIDGE-202): a brand could be absent from
 * the sitemap and every grid while its standalone page still answered 200 for
 * anyone holding the URL — including the AI crawlers this site is being
 * rebuilt around. The redirect is deliberate; a 404 would hide a possibly
 * cited page without a forwarding address, which the standing citation rule
 * forbids.
 *
 * Curated 2026-07-20, extended 2026-07-29. When adding: prefer leaving a
 * borderline real brand IN.
 */

/** Soft drinks & beer printed on licensed novelty mini-fridges. */
const BEVERAGE_BRANDS = [
  "coca-cola",
  "pepsi",
  "sprite",
  "fanta",
  "mountain-dew",
  "dr-pepper",
  "coors-light",
  "corona",
]

/** Entertainment / character IP licensed onto novelty fridges & coolers. */
const ENTERTAINMENT_IP_BRANDS = [
  "batman",
  "call-of-duty",
  "harry-potter",
  "hello-kitty",
  "minecraft",
  "naruto",
  "nfl",
  "xbox",
]

/** Automotive / tire marques — not appliance makers. */
const AUTOMOTIVE_BRANDS = [
  "ford",
  "bfgoodrich",
  "michelin",
]

/** Not brands at all — product spec tokens mis-ingested as manufacturers. */
const NON_BRAND_TOKENS = [
  "wifi",
  "inverter",
  // A stray feed cell, not a name: one product row carries the literal brand
  // "Lower". Same class as "WiFi" — arrived through FRIDGE-202's door.
  "lower",
]

/**
 * RETAILERS AND RESELLERS — the merchant selling the product, never its maker
 * (FRIDGE-202, curated 07/29/2026 from the live `brands` table).
 *
 * These reached the `brands` table because two importer paths used the feed's
 * *advertiser* as a brand fallback. Both doors are now closed and guarded by
 * `tests/retailer-name-not-brand.test.ts`, but the rows they already created
 * remain, and each one backs a public /brands/[slug] URL.
 *
 * Curation note — `cooler-depot` is deliberately NOT here. The card that
 * raised this flagged it as the one genuinely uncertain case, and it is a real
 * manufacturer: its products are sold by Wayfair AND Amazon (verified
 * 07/29/2026), and a retailer's own name does not appear as the brand on a
 * competitor's marketplace. It keeps its page and its 35 active products.
 * `cooler-depot-usa` IS here — that is the storefront-form duplicate, with
 * zero active products.
 */
const RETAILER_AND_RESELLER_NAMES = [
  "a-to-z-supply",
  "coldsupply",
  "cooler-depot-usa",
  "culinary-depot",
  "duncan-supplyscotsman",
  "elite-kitchen-supply",
  "ministry-of-warehouse",
  "restaurant-supplies-direct",
  "supplying-demand",
  "the-restaurant-warehouse",
]

/**
 * Bare domains recorded as brand names. A domain is never a brand name form,
 * whatever sits behind it: `ricambigrossclima.com` is an Italian parts
 * retailer, and Smugdesk is a real office-furniture maker — neither is a
 * refrigeration manufacturer, and neither name is written the way a brand is.
 */
const BARE_DOMAIN_SELLER_NAMES = [
  // Both spellings on purpose. The stored `brands.slug` dropped the dot
  // entirely, but `isExcludedBrand`'s name fallback slugifies a dot to a dash
  // — so a caller holding only the display name "ricambigrossclima.com"
  // derives `ricambigrossclima-com` and would sail past a list carrying just
  // the stored form. Caught by the test rather than reasoned about.
  // "Smugdesk.com" needs no twin: its stored slug is already the dashed form.
  "ricambigrossclimacom",
  "ricambigrossclima-com",
  "smugdesk-com",
]

/**
 * Full set of `brands.slug` values suppressed from every brand logo grid,
 * from /brands, from the brands sitemap, and from /brands/[slug] itself.
 */
export const EXCLUDED_BRAND_SLUGS: ReadonlySet<string> = new Set([
  ...BEVERAGE_BRANDS,
  ...ENTERTAINMENT_IP_BRANDS,
  ...AUTOMOTIVE_BRANDS,
  ...NON_BRAND_TOKENS,
  ...RETAILER_AND_RESELLER_NAMES,
  ...BARE_DOMAIN_SELLER_NAMES,
])

/**
 * True when a brand should be hidden from manufacturer/brand logo grids.
 * Accepts a slug (preferred) or falls back to slugifying a display name so
 * callers that only hold a name still get correct results.
 */
export function isExcludedBrand(brand: { slug?: string | null; name?: string | null } | string): boolean {
  if (typeof brand === "string") {
    return EXCLUDED_BRAND_SLUGS.has(brand.toLowerCase())
  }
  const slug = (brand.slug || "").toLowerCase()
  if (slug && EXCLUDED_BRAND_SLUGS.has(slug)) return true
  // Fallback: derive a slug from the name the same way brand routes do.
  const nameSlug = (brand.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return !!nameSlug && EXCLUDED_BRAND_SLUGS.has(nameSlug)
}
